(ns ductile.ui.load-builder.overview-map
  "The map shown next to the load builder"
  (:require-macros [ductile.ui.macros :refer [js-for]])
  (:require ["@turf/turf" :as turf]
            [applied-science.js-interop :as j]
            [clojure.string :as str]
            [ductile.ui.components :as ui]
            [ductile.ui.deframe :as deframe]
            [ductile.ui.mapbox :as mapbox]
            [ductile.ui.transit :as transit]
            [goog.functions]
            [nextjournal.clerk.render.hooks :as hooks]
            [nextjournal.clerk.render.localstorage :as localstorage]
            [nextjournal.log :as log]
            [reagent.core :as reagent]))

(def !the-map
  (atom nil))

(def !popup
  (atom nil))

(def !state-synced?
  (deframe/!route-get ::state-synced? false))

(def !map-center
  (deframe/!route-get ::map-center false))

(def !map-zoom
  (deframe/!route-get ::map-zoom false))

(defn other-window []
  (or
   js/window.opener
   @!popup))

(defn post-message!
  ([msg]
   (post-message! (other-window) msg))
  ([window msg]
   (when window
     (prn "post-message!" msg)
     (.postMessage window (transit/write msg)))))

(defn tx!
  ([tx]
   (tx! (other-window) tx))
  ([window tx]
   (deframe/tx! tx)
   (post-message! window [:tx tx])))

(defn on-message [msg]
  (let [[cmd & [arg1 :as args]] (transit/read (.-data msg))]
    #_(apply prn "on-message" cmd args)
    (case cmd
      :send-state
      (let [data (-> @(deframe/!get-in [])
                     (select-keys [:trucks :transports :current-route/transient])
                     (assoc-in [:current-route/transient ::state-synced?] true))]
        (post-message! [:state data]))

      :state
      (deframe/tx! [[:op/merge (first args)]])

      :tx
      (deframe/tx! arg1))))

(def address-name
  (memoize
   (fn [{:address/keys [country postcode city street]}]
     (str (:country/code country) ", " postcode " " city ", " street))))

(defn address-aggregate
  "Aggregate pickup/delivery addresses.

  Returns a map, keyed by address, with for each address the lat/long and amount of pickups/deliveries.

  ```
  {\"DE, 10247 Berlin, Warschauwerstr. 123\" {:latitude ... :longitude ... :pickups 7 :deliveries 10 :total 17}}
  ```
  "
  [transports]
  (reduce
   (fn [acc {:transport/keys [origin destination]}]
     (-> acc
         (update (address-name origin)
                 #(-> %
                      (assoc :location/latitude (:location/latitude origin)
                             :location/longitude (:location/longitude origin)
                             :address/postcode (:address/postcode origin)
                             :address/city (:address/city origin)
                             :address/street (:address/street origin)
                             :address/name (:address/name origin))
                      (update :pickups (fnil inc 0))
                      (update :total (fnil inc 0))))
         (update (address-name destination)
                 #(-> %
                      (assoc :location/latitude (:location/latitude destination)
                             :location/longitude (:location/longitude destination)
                             :address/postcode (:address/postcode destination)
                             :address/city (:address/city destination)
                             :address/street (:address/street destination)
                             :address/name (:address/name destination))
                      (update :total (fnil inc 0))
                      (update :deliveries (fnil inc 0))))))
   {}
   transports))

#_(address-aggregate ductile.ui.load-builder/transports-sample)

(defn locations-as-feature-coll [data]
  (turf/featureCollection
   (.filter
    (js-for [[addr-name {:keys [address/postcode address/city address/street address/name location/latitude location/longitude pickups deliveries total]}]
             (sort-by (comp :total val) (address-aggregate data))]
            (when (and longitude latitude)
              (j/lit {:type "Feature"
                      :id addr-name
                      :geometry {:type "Point"
                                 :coordinates [longitude latitude]}
                      :properties {:title addr-name
                                   :id addr-name
                                   :pickup (or pickups 0)
                                   :postcode postcode
                                   :city city
                                   :street street
                                   :name name
                                   :delivery (or deliveries 0)
                                   :icon_variant (count (str (or pickups deliveries 0)))
                                   :sum total}})))
    some?)))

(defn cluster-geojson [data]
  (j/lit {:type "geojson"
          :data (locations-as-feature-coll data)
          :cluster true
          :clusterMaxZoom 6
          :clusterRadius 30
          :clusterProperties {:delivery ["+" ["get" "delivery"]]
                              :pickup ["+" ["get" "pickup"]]}}))

#_(cluster-geojson ductile.ui.load-builder/transports-sample)

(defn transport-id [{:transport/keys [origin destination]}]
  (str (address-name origin) "#" (address-name destination)))

(defn get-flows [data]
  (turf/featureCollection
   (js-for [[id vehicles] (group-by transport-id data)
            :let [{:transport/keys [origin destination]} (first vehicles)
                  ls (turf/lineString (j/lit [[(:location/longitude origin) (:location/latitude origin)]
                                              [(:location/longitude destination) (:location/latitude destination)]]))]]

           (j/lit {:type "Feature"
                   :geometry (.-geometry ls)
                   :properties {:count (count vehicles)
                                :id id}}))))

(defn add-map-sources [data ^js m]
  (.addSource m "locationClusters" (cluster-geojson data))
  (.addSource m "flows" (j/lit {:type "geojson"
                                :lineMetrics true
                                :data (get-flows data)})))

(def line-dashes
  [[0 4 3]
   [0.5 4 2.5]
   [1 4 2]
   [1.5 4 1.5]
   [2 4 1]
   [2.5 4 0.5]
   [3 4 0]
   [0 0.5 3 3.5]
   [0 1 3 3]
   [0 1.5 3 2.5]
   [0 2 3 2]
   [0 2.5 3 1.5]
   [0 3 3 1]
   [0 3.5 3 0.5]])

(def !step (atom 0))
(def !route-line-animation (atom nil))

(defn animate-route-line! [^js m timestamp]
  (when @!the-map
    (let [new-step (js/parseInt (rem (/ timestamp 50) (count line-dashes)))]
      (when-not (= new-step @!step)
        (.setPaintProperty m "route-line-dashed" "line-dasharray" (clj->js (nth line-dashes @!step)))
        (reset! !step new-step))
      (reset! !route-line-animation (js/requestAnimationFrame (partial animate-route-line! m))))))

(defn update-route-layer [route ^js m]
  (let [visibility (if (some? route) "visible" "none")]
    (.setLayoutProperty m "route-line-border" "visibility" visibility)
    (.setLayoutProperty m "route-line-background" "visibility" visibility)
    (.setLayoutProperty m "route-line-dashed" "visibility" visibility)
    (.setLayoutProperty m "route-home" "visibility" visibility)
    (.setLayoutProperty m "route-stop-background" "visibility" visibility)
    (.setLayoutProperty m "route-stop" "visibility" visibility)
    (.setLayoutProperty m "route-stop-count" "visibility" visibility)
    (if (some? route)
      (animate-route-line! m 0)
      (do
        (js/cancelAnimationFrame @!route-line-animation)
        (reset! !step 0)
        (reset! !route-line-animation nil)))))

(defn add-route-sources [{:as route :keys [line stops]} ^js m]
  (.addSource m "routeLine" (j/lit {:type :geojson :data line}))
  (.addSource m "routeStops" (j/lit {:type :geojson :data stops})))

(defn update-map-sources [data ^js m]
  (.setData (.getSource m "locationClusters") (locations-as-feature-coll data))
  (.setData (.getSource m "flows") (get-flows data)))

(defn update-route-sources [{:as route :keys [line stops]} ^js m]
  (.setData (.getSource m "routeLine") line)
  (update-route-layer route m)
  (.setData (.getSource m "routeStops") stops))

(defn fit-route-in-view! [{:keys [line stops]} ^js m]
  (when (and m (or line stops))
    (.fitBounds m
                (turf/bbox (or line stops))
                #js {:padding #js {:top 20 :right 20 :bottom 20 :left 20}
                     :linear true
                     :duration 250})))

(def min-radius 10)

(defn point-filter [prop]
  (j/lit [">" ["get" prop] 0]))

(defn text-offset-at-zoom-level [factor]
  (j/lit
   ["+" 1 ["*"
           ["max"
            ["sqrt" ["*"
                     ["max" ["get" "delivery"] ["get" "pickup"]]
                     factor]]
            min-radius]
           (/ 1 12)]]))

(defn count-layout [prop]
  (j/lit
   {"text-field" ["get" prop]
    "text-anchor" "center"
    "text-size" 12
    "text-allow-overlap" true
    "text-ignore-placement" true
    "text-justify" "auto"}))

(defn flow-line-width [^js m]
  (let [flows (.. (.getSource m "flows") -_data -features (map #(.. % -properties -count)))]
    (/ (.reduce flows #(+ %1 %2) 0) (.-length flows))))

(defn marker-with-variants [& variants]
  (let [var-str (str/join "-" variants)]
    (clj->js
     ["case"
      ["==" ["get" "icon_variant"] 1] (str "unclustered-" var-str "-n")
      ["==" ["get" "icon_variant"] 2] (str "unclustered-" var-str "-nn")
      (str "unclustered-" var-str "-nnn")])))

(defn add-map-layers [theme ^js m]
  (.addLayer m (j/lit {:id "flowLines"
                       :type "line"
                       :source "flows"
                       :layout {"line-join" "round"
                                "line-cap" "round"
                                "visibility" "none"}
                       :paint {"line-color" (get-in theme [:vis "delivery"])
                               "line-opacity" 0.3
                               "line-width" 10
                               "line-gradient" ["interpolate"
                                                ["linear"]
                                                ["line-progress"]
                                                0
                                                (get-in theme [:vis "pickup"])
                                                1
                                                (get-in theme [:vis "delivery"])]}}))
  (.addLayer m (j/lit {:id "locationClustersPickup"
                       :type "circle"
                       :source "locationClusters"
                       :layout {:icon-allow-overlap true}
                       :paint (j/lit {"circle-radius" ["step" ["get" "pickup"] 10 10 15 30 20 50 25]
                                      "circle-color" (get-in theme [:vis "pickup"])
                                      "circle-stroke-color" "#ffffff"
                                      "circle-stroke-width" 2})
                       :filter ["all" [">" ["get" "pickup"] 0] ["has" "point_count"]]}))
  (.addLayer m (j/lit {:id "locationsCountPickup"
                       :type "symbol"
                       :source "locationClusters"
                       :layout (count-layout "pickup")
                       :paint {"text-color" "#ffffff" #_(get-in theme [:vis "pickup"])}
                       :filter ["all" [">" ["get" "pickup"] 0] ["has" "point_count"]]}))
  (.addLayer m (j/lit {:id "locationClustersDelivery"
                       :type "circle"
                       :source "locationClusters"
                       :layout {:icon-allow-overlap true}
                       :paint (j/lit {"circle-radius" ["step" ["get" "delivery"] 10 10 15 30 20 50 25]
                                      "circle-color" ["case"
                                                      ["boolean" ["feature-state" "hover"] false] (get-in theme [:vis :hover "delivery"])
                                                      ["boolean" ["feature-state" "selected"] false] (get-in theme [:vis :selected "delivery"])
                                                      (get-in theme [:vis "delivery"])]
                                      "circle-stroke-color" ["case"
                                                             ["boolean" ["feature-state" "selected"] false] "#34d29a"
                                                             "#ffffff"]
                                      "circle-stroke-width" 2})
                       :filter ["all" [">" ["get" "delivery"] 0] ["has" "point_count"]]}))
  (.addLayer m (j/lit {:id "locationsCountDelivery"
                       :type "symbol"
                       :source "locationClusters"
                       :layout (count-layout "delivery")
                       :paint {"text-color" "#ffffff"}
                       :filter ["all" [">" ["get" "delivery"] 0] ["has" "point_count"]]}))
  (doseq [icon ["unclustered-delivery-n" "unclustered-delivery-nn" "unclustered-delivery-nnn"
                "unclustered-delivery-hover-n" "unclustered-delivery-hover-nn" "unclustered-delivery-hover-nnn"
                "unclustered-delivery-selected-n" "unclustered-delivery-selected-nn" "unclustered-delivery-selected-nnn"
                "unclustered-pickup-n" "unclustered-pickup-nn" "unclustered-pickup-nnn" "route-stop-home"]]
    (.loadImage m (ui/maybe-add-host (str "/images/" icon ".png"))
                (fn [error image]
                  (if error
                    (js/console.log "Couldn't load marker image:" error)
                    (.addImage m icon image)))))
  (.addLayer m (j/lit {:id "unclusteredLocationsDelivery"
                       :type "symbol"
                       :source "locationClusters"
                       :layout {:icon-image (marker-with-variants "delivery")
                                :icon-allow-overlap true
                                :icon-size 0.8}
                       :filter ["all" [">" ["get" "delivery"] 0] ["!" ["has" "point_count"]]]}))
  (.addLayer m (j/lit {:id "unclusteredCountDelivery"
                       :type "symbol"
                       :source "locationClusters"
                       :layout {"text-field" ["get" "delivery"]
                                "text-anchor" "center"
                                "text-size" 11
                                "text-offset" [0 -0.2]
                                "text-allow-overlap" true
                                "text-ignore-placement" true
                                "text-justify" "auto"}
                       :paint {"text-color" "#ffffff"}
                       :filter ["all" [">" ["get" "delivery"] 0] ["!" ["has" "point_count"]]]}))
  (.addLayer m (j/lit {:id "unclusteredLocationsPickup"
                       :type "symbol"
                       :source "locationClusters"
                       :layout {:icon-image (marker-with-variants "pickup")
                                :icon-allow-overlap true
                                :icon-size 0.8}
                       :filter ["all" [">" ["get" "pickup"] 0] ["!" ["has" "point_count"]]]}))
  (.addLayer m (j/lit {:id "unclusteredCountPickup"
                       :type "symbol"
                       :source "locationClusters"
                       :layout {"text-field" ["get" "pickup"]
                                "text-anchor" "center"
                                "text-size" 11
                                "text-offset" [0 -0.2]
                                "text-allow-overlap" true
                                "text-ignore-placement" true
                                "text-justify" "auto"}
                       :paint {"text-color" "#ffffff"}
                       :filter ["all" [">" ["get" "pickup"] 0] ["!" ["has" "point_count"]]]}))
  (.addLayer m (j/lit {:id "route-line-border"
                       :source "routeLine"
                       :type "line"
                       :paint {"line-color" "white"
                               "line-width" 12}}))
  (.addLayer m (j/lit {:id "route-line-background"
                       :source "routeLine"
                       :type "line"
                       :paint {"line-color" "red"
                               "line-width" 8
                               "line-opacity" 0.4}}))
  (.addLayer m (j/lit {:id "route-line-dashed"
                       :source "routeLine"
                       :type "line"
                       :paint {"line-color" "red"
                               "line-width" 8
                               "line-dasharray" [0 4 3]}}))
  (.addLayer m (j/lit {:id "route-home"
                       :type "symbol"
                       :source "routeStops"
                       :layout {:icon-image "route-stop-home"
                                :icon-allow-overlap true
                                :icon-size 0.5}
                       :filter ["==" ["get" "index"] 0]}))
  (.addLayer m (j/lit {:id "route-stop-background"
                       :type "circle"
                       :source "routeStops"
                       :paint (j/lit {"circle-radius" 15
                                      "circle-color" "#ffffff"})
                       :filter [">" ["get" "index"] 0]}))
  (.addLayer m (j/lit {:id "route-stop"
                       :type "circle"
                       :source "routeStops"
                       :paint (j/lit {"circle-radius" 11
                                      "circle-color" "#fee2e2"
                                      "circle-stroke-color" "red"
                                      "circle-stroke-width" 2})
                       :filter [">" ["get" "index"] 0]}))
  (.addLayer m (j/lit {:id "route-stop-count"
                       :type "symbol"
                       :source "routeStops"
                       :layout (j/assoc! (count-layout "index")
                                         :text-font (j/lit ["Inter Bold"
                                                            "Arial Unicode MS Bold"])
                                         :text-offset (j/lit [0 0.07])
                                         :text-size 14)
                       :paint {"text-color" "red"}
                       :filter [">" ["get" "index"] 0]})))

(defn update-flow-lines [^js m visible?]
  (.setLayoutProperty m "flowLines" "visibility" (if visible? "visible" "none")))

(defn selected-markers-condition [selected-marker-ids]
  (if (seq selected-marker-ids)
    (clj->js
     (concat ["case"]
             (mapcat (fn [id]
                       [["==" ["get" "id"] id] (marker-with-variants "delivery" "selected")])
                     selected-marker-ids)
             [(marker-with-variants "delivery")]))
    (marker-with-variants "delivery")))

(defn render-feature-html [^js properties]
  (str "<strong>" (.-name properties) "</strong><br>"
       (.-street properties) "<br>"
       (.-postcode properties) " " (.-city properties)))

(defn render-feature-list-html [^js features]
  (str
   "<div style='max-height: 250px; overflow-y: auto;'>"
   (str/join
    "<br><br>"
    (map #(render-feature-html (.-properties %)) features))
   "</div>"))

(defn on-map-move [^js the-map]
  (let [bounds (mapbox/get-bounds the-map)
        center [(.-lng (.getCenter the-map))
                (.-lat (.getCenter the-map))]
        zoom   (.getZoom the-map)]
    (tx! [[:op/assoc-in [:current-route/transient :ductile.ui.load-builder/filters :bounds] bounds]
          [:op/assoc-in [:current-route/transient ::map-center] center]
          [:op/assoc-in [:current-route/transient ::map-zoom] zoom]])))

(defn on-load [{:keys [route data show-flows? theme on-load-fn]} ^js the-map]
  (let [popup (js/mapboxgl.Popup. #js {:closeButton false :closeOnClick false :offset 10 :className "locations-popup"})]
    (reset! !the-map the-map)
    (.resize the-map)
    (if (and @!map-center @!map-zoom) ;; carry over from another window
      (do
        (.setCenter the-map (clj->js @!map-center))
        (.setZoom the-map @!map-zoom))
      (when (or (seq data) route)
        (.fitBounds the-map
                    (turf/bbox (cond
                                 (seq data) (locations-as-feature-coll data)
                                 (:stops route) (:stops route)))
                    #js {:padding 20 :linear true :duration 0})))
    (.resize the-map)
    (on-map-move the-map)
    (add-map-sources data the-map)
    (add-route-sources route the-map)
    (add-map-layers theme the-map)
    (update-route-layer route the-map)
    (update-flow-lines the-map show-flows?)
    (.addControl the-map (js/mapboxgl.NavigationControl.))
    (.on the-map "mouseenter" "unclusteredLocationsDelivery"
         (fn [event]
           (when-let [id (j/get-in event [:features 0 :properties :id])]
             (j/assoc-in! (.getCanvas the-map) [:style :cursor] "pointer")
             (let [selected-marker-ids @(deframe/!route-get :ductile.ui.load-builder/selected-marker-ids)
                   coords (.slice (j/get-in event [:features 0 :geometry :coordinates]))
                   popup-content (js/document.createElement "div")]
               (j/assoc! popup-content :innerHTML (render-feature-html (j/get-in event [:features 0 :properties])))
               (.. popup
                   (setLngLat coords)
                   (setDOMContent popup-content)
                   (addTo the-map))
               (.addEventListener popup-content "mouseleave" #(.remove popup))
               (when-not (contains? selected-marker-ids id)
                 (.setLayoutProperty the-map "unclusteredLocationsDelivery" "icon-image"
                                     (j/lit ["match" ["get" "id"] id (marker-with-variants "delivery" "hover")
                                             (selected-markers-condition selected-marker-ids)])))))))
    (.on the-map "mouseleave" "unclusteredLocationsDelivery"
         (fn [event]
           (let [selected-marker-ids @(deframe/!route-get :ductile.ui.load-builder/selected-marker-ids)
                 !mouse-coords (atom nil)
                 on-mouse-move (fn [e] (reset! !mouse-coords [(.-clientX e) (.-clientY e)]))]
             (js/document.addEventListener "mousemove" on-mouse-move)
             (j/assoc-in! (.getCanvas the-map) [:style :cursor] "")
             (js/setTimeout (fn []
                              (let [[x y] @!mouse-coords]
                                (when-let [el (js/document.elementFromPoint x y)]
                                  (when-not (.closest el ".locations-popup")
                                    (js/document.removeEventListener "mousemove" on-mouse-move)
                                    (.remove popup)))))
                            100)
             (.setLayoutProperty the-map "unclusteredLocationsDelivery" "icon-image"
                                 (selected-markers-condition selected-marker-ids)))))
    (.on the-map "mousemove" "locationClustersDelivery"
         (fn [event]
           (when-let [id (j/get-in event [:features 0 :id])]
             (let [coords (.slice (j/get-in event [:features 0 :geometry :coordinates]))
                   point-count (j/get-in event [:features 0 :properties :point_count])
                   popup-content (js/document.createElement "div")]
               (.. the-map
                   (getSource "locationClusters")
                   (getClusterLeaves id point-count 0 (fn [err features]
                                                        (when-not err
                                                          (j/assoc! popup-content :innerHTML (render-feature-list-html features))
                                                          (.. popup
                                                              (setLngLat coords)
                                                              (setDOMContent popup-content)
                                                              (addTo the-map))))))
               (.addEventListener popup-content "mouseleave" #(.remove popup))
               (j/assoc-in! (.getCanvas the-map) [:style :cursor] "pointer")
               (tx! [[:op/assoc-in [:current-route/transient ::mapbox-hover-id] id]])
               (.setFeatureState the-map
                                 (j/lit {:source "locationClusters" :id id})
                                 (j/lit {:hover true}))))))
    (.on the-map "mouseleave" "locationClustersDelivery"
         (fn [event]
           (let [!mouse-coords (atom nil)
                 on-mouse-move (fn [e] (reset! !mouse-coords [(.-clientX e) (.-clientY e)]))]
             (js/document.addEventListener "mousemove" on-mouse-move)
             (j/assoc-in! (.getCanvas the-map) [:style :cursor] "")
             (js/setTimeout (fn []
                              (let [[x y] @!mouse-coords]
                                (when-let [el (js/document.elementFromPoint x y)]
                                  (when-not (.closest el ".locations-popup")
                                    (js/document.removeEventListener "mousemove" on-mouse-move)
                                    (.remove popup)))))
                            100)
             (when-some [mapbox-hover-id @(deframe/!route-get ::mapbox-hover-id)]
               (.setFeatureState the-map
                                 (j/lit {:source "locationClusters" :id mapbox-hover-id})
                                 (j/lit {:hover false}))))))
    (on-load-fn the-map)))

(defn icon-popup-open []
  [:svg {:width 29
         :height 29
         :viewBox "0 0 58 58"
         :fill "none"
         :stroke "#111"
         :stroke-width 4.5
         :xmlns "http://www.w3.org/2000/svg"}
   [:path {:d "M32 38V38C32 39.6569 30.6569 41 29 41H20C18.3431 41 17 39.6569 17 38V29C17 27.3431 18.3431 26 20 26V26"}]
   [:rect {:x 25 :y 15 :width 18 :height 18 :rx 3}]])

(defn icon-popup-close []
  [:svg {:width 29
         :height 29
         :viewBox "0 0 58 58"
         :fill "none"
         :stroke "#111"
         :stroke-width 4.5
         :xmlns "http://www.w3.org/2000/svg"}
   [:path {:d "M32 18V17C32 15.8954 31.1046 15 30 15H27M20 30H19C17.8954 30 17 29.1046 17 28V25M17 20V17C17 15.8954 17.8954 15 19 15H22"}]
   [:rect {:x 25 :y 23 :width 18 :height 18 :rx 2}]])

(defn open-popup! []
  (let [el           (js/document.querySelector ".mapboxgl-map")
        pathname     js/location.pathname
        location     (str js/location.origin pathname "/popup-map")
        map-pos      (localstorage/get-item "load-builder-map-pos")
        opts         (str "popup=true"
                          ",innerWidth="  (or (:w map-pos) (.-clientWidth el))
                          ",innerHeight=" (or (:h map-pos) (.-clientHeight el))
                          (when-some [x (:x map-pos)]
                            (str ",left=" x))
                          (when-some [y (:y map-pos)]
                            (str ",top=" y)))
        !timer       (atom nil)
        check-closed (fn []
                       (cond
                         (nil? @!popup)
                         (some-> !timer deref js/clearInterval)

                         (or
                          (not= pathname js/location.pathname)
                          (.-closed @!popup))
                         (do
                           (some-> !timer deref js/clearInterval)
                           (when-not (.-closed @!popup)
                             (.close @!popup))
                           (reset! !popup nil)
                           (.removeEventListener js/window "message" on-message)
                           (when (= pathname js/location.pathname)
                             (tx! [[:op/assoc-in [:current-route/transient :ductile.ui.load-builder/show-map?] true]])))))]
    (when-some [popup (js/window.open location nil opts)]
      (reset! !popup popup)
      (tx! [[:op/assoc-in [:current-route/transient :ductile.ui.load-builder/show-map?] false]])
      (.addEventListener js/window "message" on-message)
      (reset! !timer (js/setInterval check-closed 100)))))

(defn view [{:as config
             :keys [standalone? rounded? shadow? border? show-popup-button? fit-route-key
                    data theme show-flows? route on-load-fn]
             :or {standalone? true
                  shadow? true
                  border? true
                  show-popup-button? true
                  on-load-fn  #()}}]
  (reagent/with-let [on-map-move' (goog.functions/debounce on-map-move 100)]
    (let [!map-ref (hooks/use-ref nil)
          !last-fit-key (hooks/use-ref nil)
          rounded? (if (contains? config :rounded?)
                     rounded?
                     (not standalone?))]
      (mapbox/use-map !map-ref {:on-load   #(on-load {:data        data
                                                      :route       route
                                                      :show-flows? show-flows?
                                                      :theme       theme
                                                      :on-load-fn  on-load-fn} %)
                                :on-remove #(reset! !the-map nil)
                                :on-zoom   on-map-move'
                                :on-drag   on-map-move'
                                :center    [13 52]
                                :zoom      5})
      (hooks/use-effect #(when-some [the-map @!the-map]
                           (update-map-sources data the-map)) [data])
      (hooks/use-effect #(when-some [the-map @!the-map]
                           (update-flow-lines the-map show-flows?)) [show-flows?])
      (hooks/use-effect
       #(when-some [the-map @!the-map]
          (update-route-sources route the-map)
          (let [has-fit-key? (contains? config :fit-route-key)]
            (cond
              (nil? route)
              (set! (.-current !last-fit-key) nil)

              has-fit-key?
              (when (not= (.-current !last-fit-key) fit-route-key)
                (fit-route-in-view! route the-map)
                (set! (.-current !last-fit-key) fit-route-key))

              :else
              (fit-route-in-view! route the-map))))
       [route fit-route-key config])
      [:div.relative.h-full.overflow-hidden
       [:div.bg-slate-300.flex-auto.h-full
        {:ref   !map-ref
         :class [(when rounded? "rounded-md")
                 (when border? "border border-slate-300")
                 (when shadow? "shadow")]
         :style {:height (if standalone? "100vh" (or (:layout-height config) "100%"))}}]
       (if standalone?
         [:button.bg-white.absolute.flex.items-center.justify-center
          {:class ["left-[10px]" "top-[10px]" "rounded-[4px]" "w-[29px]" "h-[29px]"
                   "shadow-[0_0_0_2px_rgba(0,0,0,0.1)]" "hover:bg-[#F2F2F2]"]
           :on-click #(js/window.close)}
          [icon-popup-close]]
         (when show-popup-button?
           [:button.bg-white.absolute.flex.items-center.justify-center
            {:class ["left-[10px]" "top-[10px]" "rounded-[4px]" "w-[29px]" "h-[29px]"
                     "shadow-[0_0_0_2px_rgba(0,0,0,0.1)]" "hover:bg-[#F2F2F2]"]
             :on-click open-popup!}
            [icon-popup-open]]))])))
